/**
 * Microsoft Clarity client API helpers.
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api
 */

declare global {
  interface Window {
    clarity?: (action: string, ...args: (string | string[] | undefined)[]) => void
  }
}

function getClarity(): Window['clarity'] {
  return typeof window !== 'undefined' ? window.clarity : undefined
}

export type ClarityUser = {
  id: string
  email?: string
}

/**
 * Set custom identifiers for the session.
 * @param userId - Required. Your custom user/session identifier (e.g. user sub).
 * @param friendlyName - Optional label shown in the Clarity dashboard (e.g. email).
 */
export function clarityIdentify(userId: string, friendlyName?: string): void {
  const clarity = getClarity()
  if (!clarity) return
  const hint = friendlyName?.trim()
  if (hint) {
    clarity('identify', userId, undefined, undefined, hint)
  } else {
    clarity('identify', userId)
  }
}

/**
 * Bind the logged-in user to Clarity: custom user ID (identify) plus filterable tags.
 */
export function claritySetUser(user: ClarityUser | null): void {
  const clarity = getClarity()
  if (!clarity) return

  if (!user?.id) {
    clarityIdentify('anonymous')
    return
  }

  const email = user.email?.trim()
  clarityIdentify(user.id, email)
  clarity('set', 'userId', user.id)
  if (email) {
    clarity('set', 'email', email)
  }
}
/**
 * Set consent for the session.
 */
export function clarityConsent(): void {
  const clarity = getClarity()
  if (!clarity) return
  clarity('consent')
}
/**
 * Set custom tags for filtering in Clarity (Filters, Dashboard, Recordings).
 * Value can be a string or array of strings.
 */
export function claritySet(key: string, value: string | string[]): void {
  const clarity = getClarity()
  if (!clarity) return
  clarity('set', key, value)
}

/**
 * Track a custom event (appears with Smart events in Clarity).
 */
export function clarityEvent(eventName: string): void {
  const clarity = getClarity()
  if (!clarity) return
  clarity('event', eventName)
}
